import React, { useState, useEffect } from 'react';
import notificationService from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';

const NotificationSettings = () => {
  const { currentUser } = useAuth();
  const [preferences, setPreferences] = useState({
    events: true,
    cultes: true,
    anniversaires: true,
    announcements: true,
    oneDayBefore: true,
    oneHourBefore: true
  });
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPreferences();
    checkNotificationStatus();
  }, [currentUser]);

  const loadPreferences = async () => {
    if (!currentUser) return;

    try {
      const prefs = await notificationService.getUserNotificationPreferences(currentUser.uid);
      if (prefs) {
        setPreferences(prefs);
      }
    } catch (error) {
      logger.error('Erreur lors du chargement des préférences:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkNotificationStatus = () => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  };

  const enableNotifications = async () => {
    try {
      await notificationService.initialize();
      const token = await notificationService.requestPermission(currentUser.uid);
      
      if (token) {
        setNotificationsEnabled(true);
        alert('✅ Notifications activées avec succès !');
      } else {
        alert('❌ Impossible d\'activer les notifications. Veuillez vérifier les paramètres de votre navigateur.');
      }
    } catch (error) {
      logger.error('Erreur lors de l\'activation des notifications:', error);
      alert('❌ Erreur lors de l\'activation des notifications');
    }
  };

  const handlePreferenceChange = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await notificationService.updateUserNotificationPreferences(currentUser.uid, preferences);
      alert('✅ Préférences enregistrées avec succès !');
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde des préférences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          ⚙️ Paramètres de Notifications
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Gérez vos préférences de notifications pour les cultes et événements
        </p>
      </div>

      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
              Notifications Push
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {notificationsEnabled 
                ? '✅ Activées - Vous recevrez des rappels pour les événements'
                : '❌ Désactivées - Activez pour recevoir des rappels'
              }
            </p>
          </div>
          {!notificationsEnabled && (
            <button
              onClick={enableNotifications}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              Activer
            </button>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
          Types de notifications à recevoir
        </h3>
        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <input
              type="checkbox"
              checked={preferences.cultes}
              onChange={() => handlePreferenceChange('cultes')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <div>
              <span className="font-medium text-gray-800 dark:text-white">Cultes dominicaux</span>
              <p className="text-sm text-gray-500 dark:text-gray-400">Rappels pour les cultes du dimanche</p>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <input
              type="checkbox"
              checked={preferences.events}
              onChange={() => handlePreferenceChange('events')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <div>
              <span className="font-medium text-gray-800 dark:text-white">Événements spéciaux</span>
              <p className="text-sm text-gray-500 dark:text-gray-400">Conférences, fêtes, missions</p>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <input
              type="checkbox"
              checked={preferences.anniversaires}
              onChange={() => handlePreferenceChange('anniversaires')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <div>
              <span className="font-medium text-gray-800 dark:text-white">Anniversaires</span>
              <p className="text-sm text-gray-500 dark:text-gray-400">Anniversaires des membres</p>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <input
              type="checkbox"
              checked={preferences.announcements}
              onChange={() => handlePreferenceChange('announcements')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <div>
              <span className="font-medium text-gray-800 dark:text-white">Annonces importantes</span>
              <p className="text-sm text-gray-500 dark:text-gray-400">Communications urgentes de l'église</p>
            </div>
          </label>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
          Quand recevoir les rappels
        </h3>
        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <input
              type="checkbox"
              checked={preferences.oneDayBefore}
              onChange={() => handlePreferenceChange('oneDayBefore')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <div>
              <span className="font-medium text-gray-800 dark:text-white">1 jour avant</span>
              <p className="text-sm text-gray-500 dark:text-gray-400">Rappel la veille à 20h</p>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <input
              type="checkbox"
              checked={preferences.oneHourBefore}
              onChange={() => handlePreferenceChange('oneHourBefore')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <div>
              <span className="font-medium text-gray-800 dark:text-white">1 heure avant</span>
              <p className="text-sm text-gray-500 dark:text-gray-400">Rappel une heure avant le début</p>
            </div>
          </label>
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={savePreferences}
          disabled={saving}
          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer les préférences'}
        </button>
      </div>

      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
        <p className="text-sm text-yellow-800 dark:text-yellow-200">
          <strong>?? Note :</strong> Pour recevoir les notifications, assurez-vous que votre navigateur autorise les notifications pour ce site.
        </p>
      </div>
    </div>
  );
};

export default NotificationSettings;